// @flow

import queryString from 'querystring'

function getAppName(integration: IntegrationCPHCMS) {
  return integration.configuration.clientId.split(':')[0]
}

const storeHCMSAccessToken = (token) => {
  console.log('Storing HCMS Token...')
  localStorage.setItem('HCMS_ACCESS_TOKEN', token)
  const now = new Date()
  now.setHours(now.getHours() + 1)
  localStorage.setItem('HCMS_ACCESS_TOKEN_EXPIRY', now.toISOString())
}
const retreiveUnexpiredHCMSAccessToken = (): string | void => {
  const token = localStorage.getItem('HCMS_ACCESS_TOKEN')
  const expiry = localStorage.getItem('HCMS_ACCESS_TOKEN_EXPIRY')
  if (!token || !expiry) return

  const now = new Date()
  const expiryDate = new Date(expiry)
  if (expiryDate <= now) return
  console.log('Retreived Unexpired HCMS Token.')
  return token
}

async function authenticate(integration: IntegrationCPHCMS): Promise<string> {
  const unexpiredToken = retreiveUnexpiredHCMSAccessToken()
  if (!!unexpiredToken) return unexpiredToken
  const params = new FormData()
  params.append('client_id', integration.configuration.clientId)
  params.append('client_secret', integration.configuration.clientSecret)
  params.append('grant_type', 'client_credentials')
  params.append('scope', 'hcms-api')

  const response = await fetch(
    `${integration.configuration.baseUrl}/identity-server/connect/token`,
    {
      method: 'POST',
      body: params,
    }
  )
  if (!response.ok) {
    const responseText = await response.text()
    throw new Error(responseText)
  }
  const { access_token } = await response.json()
  storeHCMSAccessToken(access_token)
  return access_token
}

export async function getAsset({
  integration,
  assetId,
}: {
  integration: IntegrationCPHCMS,
  assetId: string,
}): Promise<HCMSAsset> {
  const token = await authenticate(integration)
  const url = `${integration.configuration.baseUrl}/api/apps/${getAppName(
    integration
  )}/assets/${assetId}`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  if (!response.ok) {
    const responseText = await response.text()
    throw new Error(responseText)
  }

  const asset = await response.json()
  return asset
}

async function getContentTypeById({
  integration,
  contentTypeId,
}: {
  integration: IntegrationCPHCMS,
  contentTypeId: string,
}): Promise<HCMSContentType> {
  const token = await authenticate(integration)
  const url = `${integration.configuration.baseUrl}/api/apps/${getAppName(
    integration
  )}/content-types/${contentTypeId}`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  if (!response.ok) {
    const responseText = await response.text()
    throw new Error(responseText)
  }

  const contentType = await response.json()
  return contentType
}

export async function getContentType({
  integration,
  form,
}: {
  integration: IntegrationCPHCMS,
  form: Form,
}): Promise<HCMSContentType> {
  const hcmsSubmissionEvent = form.submissionEvents.find(
    (submissionEvent) => submissionEvent.type === 'CP_HCMS'
  )
  if (!hcmsSubmissionEvent) {
    throw new Error('Form does not have a CP HCMS submission event')
  }

  const contentType = await getContentTypeById({
    integration,
    contentTypeId: hcmsSubmissionEvent.configuration.contentTypeName,
  })
  return contentType
}

export async function getContentItem({
  integration,
  contentTypeId,
  contentItemId,
}: {
  integration: IntegrationCPHCMS,
  contentTypeId: string,
  contentItemId: string,
}): Promise<{
  contentItem: HCMSContentItem,
  contentType: HCMSContentType,
}> {
  const contentType = await getContentTypeById({
    integration,
    contentTypeId,
  })

  const token = await authenticate(integration)
  const url = `${integration.configuration.baseUrl}/api/content/${getAppName(
    integration
  )}/${contentType.name}/${contentItemId}`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  if (!response.ok) {
    const responseText = await response.text()
    throw new Error(responseText)
  }

  const contentItem = await response.json()
  return {
    contentItem,
    contentType,
  }
}

export async function searchContentItems({
  integration,
  form,
  limit,
  offset,
  startDate,
  endDate,
}: {
  integration: IntegrationCPHCMS,
  form: Form,
  limit: number,
  offset: number,
  startDate: string,
  endDate: string,
}): Promise<{
  contentItems: HCMSContentItem[],
  total: number,
  nextOffset: number,
}> {
  const hcmsSubmissionEvent = form.submissionEvents.find(
    (submissionEvent) => submissionEvent.type === 'CP_HCMS'
  )
  if (!hcmsSubmissionEvent) {
    return {
      contentItems: [],
      nextOffset: 0,
      total: 0,
    }
  }

  const token = await authenticate(integration)
  const filter = `created ge ${startDate} and created le ${endDate}`
  const url = `${integration.configuration.baseUrl}/api/content/${getAppName(
    integration
  )}/${
    hcmsSubmissionEvent.configuration.contentTypeName
  }?${queryString.stringify({
    $top: limit,
    $skip: offset,
    $orderby: 'created',
    $filter: filter,
  })}`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  if (!response.ok) {
    const responseText = await response.text()
    throw new Error(responseText)
  }

  const { items, total } = await response.json()
  return {
    contentItems: items,
    nextOffset: offset + limit,
    total,
  }
}
